// gameAudioStore.ts
import { defineStore } from 'pinia';
import * as Tone from 'tone';
// LocalStorage key for mute preference
const MUTE_STORAGE_KEY = 'audioMutePreference';
/**
 * Use these interfaces however you like—exporting them allows you to import
 * them elsewhere in your codebase (e.g., for type-checking).
 */
export interface AudioFileDefinition {
  key: string;
  url: string;
}

export interface AudioQueueItem {
  type: 'music' | 'effect' | 'speech';
  action: () => void | Promise<void>;
}

/**
 * The main Game Audio Store.
 */
export const useGameAudioStore = defineStore('gameAudioStore', {
  // -----------------------------
  // State
  // -----------------------------
  state: () => {
    // Check if we should start muted
    const startMuted = localStorage.getItem(MUTE_STORAGE_KEY) === 'true';

    return {
      hasUserGesture: false,
      hasWarnedAboutGestureRequirement: false,
      isInitialized: false,
      isInitializing: false,
      isGameExited: false,
      isSpeechPlaying: false,
      // If starting muted, set volumes to 0, otherwise use defaults
      musicVolume: startMuted ? 0 : 2,
      effectsVolume: startMuted ? 0 : 6,
      speechVolume: startMuted ? 0 : 8,
      originalMusicVolume: 2, // Store the original music volume for restoration

      // Master mute functionality - check localStorage for saved preference
      isMasterMuted: startMuted,
      // Always store the default volumes as original, regardless of mute state
      originalVolumes: {
        music: 2,
        effects: 6,
        speech: 8,
      },

      isLoading: false,

      // Cache your audio files here once loaded
      audioBuffers: {} as Record<string, Tone.ToneAudioBuffer>,

      // Keep references to currently playing music & speech players
      activeAudio: {
        music: null as Tone.Player | null,
        speech: null as Tone.Player | null,
      },

      // Track ALL active speech players to prevent overlap
      allActiveSpeechPlayers: [] as Tone.Player[],

      // Track pending speech loading to cancel if needed
      pendingSpeechCancellation: null as AbortController | null,

      // Track the intended volume for currently playing music (for proper restoration after ducking)
      activeMusicVolume: null as number | null,

      // Queue system for audio actions that come in before initialization
      audioQueue: [] as AudioQueueItem[],
      isProcessingQueue: false,
      pendingAudioPreloads: [] as AudioFileDefinition[],

      // Track current game context for proper cleanup
      currentGameContext: null as string | null,
      isTransitioning: false,
      gameAudioBuffers: {} as Record<
        string,
        Record<string, Tone.ToneAudioBuffer>
      >,
    };
  },

  // -----------------------------
  // Getters
  // -----------------------------
  getters: {
    /**
     * Duck the music volume by ~40% if speech is playing, to ensure
     * speech (10) > effects (7) > music (5). Adjust the 0.4 factor
     * if you want stronger or gentler ducking.
     */

    // linearMusicVolume: (state) =>
    //   (state.musicVolume / 10) * MUSIC_VOLUME_MULTIPLIER,
    // linearEffectsVolume: (state) => state.effectsVolume / 10,
    // linearSpeechVolume: (state) =>
    //   (state.speechVolume / 10) * SPEECH_VOLUME_MULTIPLIER,
    duckedMusicVolume: (state) => {
      if (state.isSpeechPlaying) {
        return state.musicVolume * 0.4;
      }
      return state.musicVolume;
    },

    /**
     * Get linear values (0–1 range) from the 1–10 scale.
     * Not strictly necessary, but a helpful utility.
     */
    linearMusicVolume: (state) => state.musicVolume / 10,
    linearEffectsVolume: (state) => state.effectsVolume / 10,
    linearSpeechVolume: (state) => state.speechVolume / 10,
  },

  // -----------------------------
  // Actions
  // -----------------------------
  actions: {
    hasKnownUserGesture() {
      if (this.hasUserGesture) {
        return true;
      }

      if (typeof navigator === 'undefined') {
        return false;
      }

      const userActivation = (
        navigator as Navigator & {
          userActivation?: { hasBeenActive?: boolean; isActive?: boolean };
        }
      ).userActivation;
      return Boolean(userActivation?.isActive || userActivation?.hasBeenActive);
    },

    warnGestureRequired(operation: string) {
      if (this.hasWarnedAboutGestureRequirement) {
        return;
      }

      this.hasWarnedAboutGestureRequirement = true;
      console.debug(
        `Audio is not ready for ${operation}. Waiting for a user interaction to initialize Tone.`
      );
    },
    enqueueDeferredAudioPreloads(files: AudioFileDefinition[]) {
      if (!files.length) {
        return;
      }

      const pendingKeys = new Set(
        this.pendingAudioPreloads.map((file) => file.key)
      );

      files.forEach((file) => {
        if (!file?.key || !file?.url) {
          return;
        }
        if (this.audioBuffers[file.key] || pendingKeys.has(file.key)) {
          return;
        }
        this.pendingAudioPreloads.push(file);
        pendingKeys.add(file.key);
      });
    },
    async flushDeferredAudioPreloads() {
      if (!this.isInitialized || this.isInitializing) {
        return;
      }
      if (this.pendingAudioPreloads.length === 0) {
        return;
      }

      const filesToPreload = [...this.pendingAudioPreloads];
      this.pendingAudioPreloads = [];
      await this.initializeAudioBuffers(filesToPreload);
    },

    isExpectedInvalidStateError(error: unknown) {
      if (
        typeof DOMException !== 'undefined' &&
        error instanceof DOMException &&
        error.name === 'InvalidStateError'
      ) {
        return true;
      }

      if (
        error instanceof Error &&
        (error.name === 'InvalidStateError' ||
          error.message.includes('InvalidStateError'))
      ) {
        return true;
      }

      return String(error).includes('InvalidStateError');
    },
    /**
     * 1) Initialize Audio (must be triggered by a user gesture in most browsers)
     */
    async initializeAudio() {
      try {
        this.isInitializing = true;
        // Start the Tone.js context to allow playback
        await Tone.start();
        this.isInitialized = true;
        this.hasUserGesture = true;
      } catch (error) {
        console.error('Error during audio initialization:', error);
      } finally {
        this.isInitializing = false;
      }
      if (this.isInitialized && this.audioQueue.length > 0) {
        this.processAudioQueue();
      }
      if (this.isInitialized && this.pendingAudioPreloads.length > 0) {
        this.flushDeferredAudioPreloads();
      }

      return;
    },

    /**
     * 2) Handle the user's first interaction (gesture).
     * If not initialized, this can call initializeAudio automatically.
     */
    async handleUserInteraction() {
      const userActivation =
        typeof navigator !== 'undefined'
          ? (
              navigator as Navigator & {
                userActivation?: {
                  isActive?: boolean;
                  hasBeenActive?: boolean;
                };
              }
            ).userActivation
          : undefined;
      const hasActiveUserActivation = userActivation
        ? Boolean(userActivation.isActive || userActivation.hasBeenActive)
        : true;

      if (!this.hasUserGesture && !hasActiveUserActivation) {
        this.warnGestureRequired('audio initialization');
        return;
      }
      if (!this.hasUserGesture) {
        this.hasUserGesture = true;
      }

      // If not yet initialized, do so now.
      if (!this.isInitialized) {
        await this.initializeAudio();
      }

      if (this.isInitialized && this.audioQueue.length > 0) {
        this.processAudioQueue();
      }
      if (this.isInitialized && this.pendingAudioPreloads.length > 0) {
        this.flushDeferredAudioPreloads();
      }
    },

    /**
     * 3) Ensure the audio system is ready before playing anything.
     */
    async ensureAudioInitialized() {
      if (!this.hasKnownUserGesture()) {
        this.warnGestureRequired('audio playback');
        return;
      }
      if (!this.isInitialized && !this.isInitializing) {
        await this.initializeAudio();
      }
    },

    /**
     * 4) Load a single audio file into an audio buffer cache.
     */
    async loadAudio(key: string, url: string) {
      try {
        this.isLoading = true;
        const buffer = new Tone.ToneAudioBuffer();

        // Use the load method which returns a Promise
        await buffer.load(url);

        // Once loaded, add to audioBuffers
        this.audioBuffers[key] = buffer;
      } catch (error) {
        console.error(`Failed to load audio (${key}):`, error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 5) Play Music (queue if not initialized).
     */
    async playMusic(
      keyOrUrl: string,
      options: {
        loop?: boolean;
        volume?: number;
        loopStart?: number;
        loopEnd?: number;
        fadeInDuration?: number;
        crossFadeDuration: number;
      } = {}
    ) {
      const action = async () => {
        try {
          const { key } = await this.ensureAudioBufferLoaded(keyOrUrl);

          this.stopMusic();

          // Reset music volume to original when starting new music, but only if not muted
          if (!this.isMasterMuted) {
            this.musicVolume = this.originalMusicVolume;
          }

          // Always track the real intended volume for restoration on unmute/unduck
          // When muted, linearMusicVolume is 0, so fall back to originalVolumes
          const intendedVolume =
            options.volume ??
            (this.isMasterMuted
              ? this.originalVolumes.music / 10
              : this.linearMusicVolume);
          this.activeMusicVolume = intendedVolume;

          // When muted, create the player at volume 0 so it stays in sync
          // and can be heard immediately when unmuted
          const playerVolume = this.isMasterMuted ? 0 : intendedVolume;

          const player = this.createPlayer(key, {
            loop: options.loop ?? true,
            volume: playerVolume,
          });

          player.start();
          this.activeAudio.music = player;
        } catch (error) {
          console.error('Failed to play music:', error);
        }
      };

      if (this.isInitialized && !this.isInitializing) {
        await action();
      } else {
        this.audioQueue.push({ type: 'music', action });
      }
    },

    /**
     * 6) Play Music Immediately (bypass the queue).
     *    Useful if you need to force music right now, ignoring init checks.
     */
    playMusicImmediately(key: string) {
      if (!this.audioBuffers[key]) return;
      this.stopMusic();

      const player = new Tone.Player({
        url: this.audioBuffers[key],
        loop: true,
        volume: Tone.gainToDb(this.linearMusicVolume),
      }).toDestination();

      player.start();
      this.activeAudio.music = player;
    },

    /**
     * 7) Stop the currently playing music.
     */
    stopMusic(fadeOutDuration: number = 0) {
      const musicPlayer = this.activeAudio.music;
      if (!musicPlayer) {
        return;
      }

      if (fadeOutDuration > 0) {
        try {
          // Fade out the music
          musicPlayer.volume.rampTo(-Infinity, fadeOutDuration);
        } catch (error) {
          console.warn('Error fading out music:', error);
        }

        // Schedule stopping and disposing after fade out
        Tone.Transport.schedule(() => {
          this.cleanupPlayer(musicPlayer, { stop: true, dispose: true });
          if (this.activeAudio.music === musicPlayer) {
            this.activeAudio.music = null;
          }
          this.activeMusicVolume = null; // Clear stored volume
        }, `+${fadeOutDuration}`);
        return;
      }

      this.cleanupPlayer(musicPlayer, { stop: true, dispose: true });
      if (this.activeAudio.music === musicPlayer) {
        this.activeAudio.music = null;
      }
      this.activeMusicVolume = null; // Clear stored volume
    },

    /**
     * Fade out the currently playing music organically.
     * @param duration The duration of the fade-out in seconds.
     */
    fadeOutMusic(duration: number = 2) {
      const musicPlayer = this.activeAudio.music;
      if (!musicPlayer) {
        return Promise.resolve();
      }

      // Create a ramp to fade out the volume
      musicPlayer.volume.rampTo(-Infinity, duration);

      // Schedule the stop and disposal of the player after the fade
      Tone.Transport.schedule(() => {
        this.cleanupPlayer(musicPlayer, { stop: true, dispose: true });
        if (this.activeAudio.music === musicPlayer) {
          this.activeAudio.music = null;
        }
        this.activeMusicVolume = null;
      }, `+${duration}`);

      // Optionally, you can return a promise that resolves when the fade is complete
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, duration * 1000);
      });
    },

    /**
     * Lower the music volume by 50%. Only affects music, not effects or speech.
     */
    lowerMusicVolume() {
      if (this.activeAudio.music) {
        // Don't change volume if muted
        if (this.isMasterMuted) {
          return;
        }

        // Reduce the music volume by 50%
        this.musicVolume = this.originalMusicVolume * 0.5;

        // If using custom volume, lower it as well
        if (this.activeMusicVolume !== null) {
          this.activeMusicVolume = this.activeMusicVolume * 0.5;
        }

        // Apply the new volume to the currently playing music
        const targetVolume = this.activeMusicVolume ?? this.linearMusicVolume;
        this.activeAudio.music.volume.value = Tone.gainToDb(targetVolume);
      }
    },

    /**
     * Lower the music volume by 50% for demo mode to make voice clearer.
     * This is specifically for demo contexts where voice narration needs to be prominent.
     */
    duckMusicForDemo() {
      if (this.activeAudio.music) {
        // Don't change volume if muted
        if (this.isMasterMuted) {
          return;
        }

        // Reduce the music volume by 50% for demos
        this.musicVolume = this.originalMusicVolume * 0.5;

        // If using custom volume, duck it as well
        if (this.activeMusicVolume !== null) {
          this.activeMusicVolume = this.activeMusicVolume * 0.5;
        }

        // Apply the new volume to the currently playing music
        const targetVolume = this.activeMusicVolume ?? this.linearMusicVolume;
        this.activeAudio.music.volume.value = Tone.gainToDb(targetVolume);
      }
    },

    /**
     * Restore music volume to original level after demo.
     */
    restoreMusicVolumeFromDemo() {
      if (this.activeAudio.music) {
        // Don't change volume if muted
        if (this.isMasterMuted) {
          return;
        }

        // Restore to original volume
        this.musicVolume = this.originalMusicVolume;

        // If using custom volume, restore it as well (un-duck by multiplying by 2)
        if (this.activeMusicVolume !== null) {
          this.activeMusicVolume = this.activeMusicVolume * 2;
        }

        // Apply the restored volume to the currently playing music
        const targetVolume = this.activeMusicVolume ?? this.linearMusicVolume;
        this.activeAudio.music.volume.value = Tone.gainToDb(targetVolume);
      }
    },

    /**
     * 8) Example specialized effect: "ticket" effect
     */
    async playTicketEffect() {
      await this.playEffect('/assets/ticket-increase.mp3');
    },

    /**
     * 9) Initialize multiple audio buffers in parallel.
     */
    async initializeAudioBuffers(files: AudioFileDefinition[]) {
      if (!this.isInitialized || this.isInitializing) {
        this.enqueueDeferredAudioPreloads(files);
        return;
      }
      this.isLoading = true;
      try {
        const loadPromises = files.map((file) => {
          const buffer = new Tone.ToneAudioBuffer(file.url, () => {
            this.audioBuffers[file.key] = buffer;
          });
        });
        await Promise.all(loadPromises);
      } catch (error) {
        console.error('Error loading audio buffers:', error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 10) Preload a list of audio files (wrapper).
     */
    async preloadAudioFiles(fileList: AudioFileDefinition[]) {
      await this.initializeAudioBuffers(fileList);
    },

    /**
     * 11) Update volumes (music, effects, speech) using 1–10 scale.
     *     Then apply changes in real time to active players.
     */
    updateVolumes(musicVol: number, effectsVol: number, speechVol: number) {
      // Don't update volumes if muted - they should stay at 0
      if (this.isMasterMuted) {
        return;
      }

      this.musicVolume = musicVol;
      this.effectsVolume = effectsVol;
      this.speechVolume = speechVol;

      // Adjust active music's volume
      // Note: This overrides any custom volume that was set
      if (this.activeAudio.music) {
        // Update the stored active volume to match the new global setting
        this.activeMusicVolume = this.linearMusicVolume;
        this.activeAudio.music.volume.value = Tone.gainToDb(
          this.linearMusicVolume
        );
      }

      // Adjust active speech's volume
      if (this.activeAudio.speech) {
        this.activeAudio.speech.volume.value = Tone.gainToDb(
          this.linearSpeechVolume
        );
      }

      // Adjust active speech's volume (medium priority)
      if (this.activeAudio.speech) {
        this.activeAudio.speech.volume.value = Tone.gainToDb(
          (this.speechVolume / 10) * 0.75
        );
      }

      // Effects volume will be set when playing effects (highest priority)
    },
    isUrl(str: string): boolean {
      return str.startsWith('/') || str.startsWith('http');
    },
    // Add these helper methods to your store

    async ensureAudioBufferLoaded(
      keyOrUrl: string
    ): Promise<{ key: string; url: string }> {
      let key = keyOrUrl;
      let url = keyOrUrl;

      if (this.isUrl(keyOrUrl)) {
        // Create a unique key that includes the game context to prevent collisions
        // Extract game name from URL path like "/assets/games/MasterThief/audio/theme.mp3"
        const urlParts = keyOrUrl.split('/');
        const gameIndex = urlParts.findIndex((part) => part === 'games');
        const gameName =
          gameIndex >= 0 && gameIndex < urlParts.length - 1
            ? urlParts[gameIndex + 1]
            : 'unknown';
        const fileName = keyOrUrl.split('/').pop() || keyOrUrl;

        // Create unique key: "MasterThief_theme.mp3" instead of just "theme.mp3"
        key = `${gameName}_${fileName}`;
        url = keyOrUrl;
      } else {
        url = this.getUrlForKey(key);
      }

      if (!this.audioBuffers[key]) {
        await this.loadAudio(key, url);
      }

      if (!this.audioBuffers[key]) {
        console.error('Failed to load or find audio buffer for key:', key);
        throw new Error(`Failed to load audio buffer for key: ${key}`);
      }

      return { key, url };
    },

    createPlayer(
      key: string,
      options: { volume: number; loop?: boolean } = { volume: 1 }
    ): Tone.Player {
      return new Tone.Player({
        url: this.audioBuffers[key],
        volume: Tone.gainToDb(options.volume),
        loop: options.loop,
      }).toDestination();
    },
    cleanupPlayer(
      player: Tone.Player | null,
      options: { stop?: boolean; dispose?: boolean } = {}
    ) {
      if (!player) return;

      const { stop = true, dispose = true } = options;

      if (stop) {
        try {
          player.stop();
        } catch {
          // Ignore teardown stop races (already stopped/disposed).
        }
      }

      if (dispose) {
        try {
          player.dispose();
        } catch (error) {
          if (this.isExpectedInvalidStateError(error)) {
            return;
          }
          console.warn('Error disposing audio player:', error);
        }
      }
    },
    removeSpeechPlayerReferences(player: Tone.Player | null) {
      if (!player) return;

      const index = this.allActiveSpeechPlayers.indexOf(player);
      if (index > -1) {
        this.allActiveSpeechPlayers.splice(index, 1);
      }

      if (this.activeAudio.speech === player) {
        this.activeAudio.speech = null;
      }

      if (this.allActiveSpeechPlayers.length === 0) {
        this.setSpeechPlaying(false);
      }
    },
    /**
     * 12) Play a generic effect from the audio buffers.
     */
    async playEffect(keyOrUrl: string) {
      // Check if muted before doing anything
      if (this.isMasterMuted) {
        return;
      }
      await this.ensureAudioInitialized();
      if (!this.isInitialized) {
        return;
      }

      try {
        const { key } = await this.ensureAudioBufferLoaded(keyOrUrl);

        // Double-check mute state in case it changed while loading
        if (this.isMasterMuted) {
          return;
        }

        const player = this.createPlayer(key, {
          volume: this.linearEffectsVolume,
        });

        player.start();
      } catch (error) {
        console.error('Failed to play effect:', error);
      }
    },

    /**
     * 13) Play Speech and duck music while it is playing.
     *     Enhanced with cancellation tokens to prevent async race conditions.
     */
    async playSpeech(keyOrUrl: string) {
      await this.ensureAudioInitialized();
      if (!this.isInitialized) {
        return;
      }
      // Stop ALL existing speech first (this also cancels pending loads)
      this.stopSpeech();

      // Create cancellation token for this specific speech operation
      const abortController = new AbortController();
      this.pendingSpeechCancellation = abortController;

      try {
        const { key } = await this.ensureAudioBufferLoaded(keyOrUrl);

        // Check if this speech was cancelled during loading
        if (abortController.signal.aborted) {
          return;
        }

        // Double-check we're still the active speech request
        if (this.pendingSpeechCancellation !== abortController) {
          return;
        }

        // Play at volume 0 when muted (same as music) so unmuting reveals audio mid-clip
        const playerVolume = this.isMasterMuted ? 0 : this.linearSpeechVolume;

        // Create the new speech player
        const speechPlayer = this.createPlayer(key, {
          volume: playerVolume,
        });

        // Add to ALL players array for tracking
        this.allActiveSpeechPlayers.push(speechPlayer);

        // Also set single reference for backward compatibility
        this.activeAudio.speech = speechPlayer;

        // Start playing
        speechPlayer.start();

        // Set up cleanup when speech ends
        speechPlayer.onstop = () => {
          this.removeSpeechPlayerReferences(speechPlayer);
        };

        this.setSpeechPlaying(true);
        this.pendingSpeechCancellation = null;
      } catch (error) {
        // Only log error if not cancelled
        if (!abortController.signal.aborted) {
          console.error('Failed to play speech:', error);
        }
        this.setSpeechPlaying(false);

        // Clear cancellation reference if it's still ours
        if (this.pendingSpeechCancellation === abortController) {
          this.pendingSpeechCancellation = null;
        }
      }
    },

    // Helper method to get URL for a key (you'll need to implement this based on your setup)
    getUrlForKey(key: string): string {
      // This is a placeholder. You should implement this method to return the correct URL for a given key
      // For example, you might have a mapping of keys to URLs
      return `/assets/games/PuzzleTrain/audio/${key}`;
    },

    /**
     * Set speech playing state and handle volume ducking
     */
    setSpeechPlaying(isPlaying: boolean) {
      this.isSpeechPlaying = isPlaying;

      // Update music volume based on speech state
      if (this.activeAudio.music) {
        try {
          // When muted, always keep volume at 0
          if (this.isMasterMuted) {
            this.activeAudio.music.volume.value = Tone.gainToDb(0);
            return;
          }

          // Use stored custom volume if available, otherwise fall back to global volume
          const baseVolume = this.activeMusicVolume ?? this.linearMusicVolume;

          const targetVolume = isPlaying
            ? baseVolume * 0.4 // Duck the music to 40% of base volume
            : baseVolume; // Restore to base volume

          this.activeAudio.music.volume.value = Tone.gainToDb(targetVolume);
        } catch (error) {
          console.warn(
            'Error updating music volume for speech ducking:',
            error
          );
        }
      }
    },

    /**
     * 14) Stop any currently playing speech immediately and forcefully.
     *     Enhanced to stop ALL speech players and cancel pending operations.
     */
    stopSpeech() {
      // Stop active speech players; let Tone/GC handle one-shot disposal to avoid double-dispose races
      const playersToStop = [...this.allActiveSpeechPlayers];
      playersToStop.forEach((player) => {
        this.cleanupPlayer(player, { stop: true, dispose: false });
      });

      // Clear the array of all players
      this.allActiveSpeechPlayers = [];

      // Cancel any pending speech loading operations
      if (this.pendingSpeechCancellation) {
        this.pendingSpeechCancellation.abort();
        this.pendingSpeechCancellation = null;
      }

      // Clear the single reference (for backward compatibility)
      this.activeAudio.speech = null;
      this.setSpeechPlaying(false);
    },

    /**
     * Forcefully stop all speech - enhanced version that works with timeline coordination
     * Note: Timeline-specific speech coordination is now handled by the timeline system
     */
    forceStopAllSpeech() {
      // Stop current speech (this is sufficient for direct speech calls)
      this.stopSpeech();

      // Note: Timeline speech scheduling uses setTimeout, not Tone.js Transport
      // Timeline coordination is now handled by stopping timeline instances directly
    },

    /**
     * 15) Process queued audio actions (added when the audio wasn't ready yet).
     */
    async processAudioQueue() {
      if (this.isProcessingQueue) return;
      if (!this.isInitialized || this.isInitializing) return;
      this.isProcessingQueue = true;
      try {
        while (this.audioQueue.length > 0) {
          const item = this.audioQueue.shift();
          if (item?.action) {
            // When muted, still process music items so they play silently and
            // can be heard immediately on unmute. Skip effects/speech since
            // they're one-shot and don't need silent playback.
            if (this.isMasterMuted && item.type !== 'music') {
              continue;
            }
            await item.action();
          }
        }
      } finally {
        this.isProcessingQueue = false;
      }
    },

    /**
     * 16) Stop all audio (music, speech, possibly any effect if tracked).
     */
    stopAllAudio() {
      this.stopMusic();
      this.stopSpeech();
      // If you had references to effect players, stop them too
    },

    /**
     * 17) Mark the game as exited, and optionally do any audio cleanup.
     */
    setGameExited(value: boolean) {
      this.isGameExited = value;
      if (value) {
        // Example: stop all audio if the game is exiting
        this.stopAllAudio();
      }
    },

    /**
     * 18) Master mute functionality: mute/unmute all audio types
     */
    setMasterMute(muted: boolean) {
      if (muted === this.isMasterMuted) return; // No change needed

      // Save mute preference to localStorage
      try {
        localStorage.setItem(MUTE_STORAGE_KEY, muted.toString());
      } catch (error) {
        console.warn('Failed to save mute preference to localStorage:', error);
      }

      if (muted) {
        // Store current volumes before muting
        this.originalVolumes.music = this.musicVolume;
        this.originalVolumes.effects = this.effectsVolume;
        this.originalVolumes.speech = this.speechVolume;

        // Set all volumes to 0
        this.musicVolume = 0;
        this.effectsVolume = 0;
        this.speechVolume = 0;

        this.isMasterMuted = true;

        // Clear any queued audio to prevent it from playing when queue is processed
        this.audioQueue = [];

        // Don't stop the audio, just mute it by setting volumes to 0
        // This allows it to continue and resume when unmuted
        // The volume changes will be applied by applyVolumeChangesToActivePlayers() below
      } else {
        // Restore original volumes
        this.musicVolume = this.originalVolumes.music;
        this.effectsVolume = this.originalVolumes.effects;
        this.speechVolume = this.originalVolumes.speech;

        this.isMasterMuted = false;
      }

      // Apply volume changes to active audio players
      this.applyVolumeChangesToActivePlayers();
    },

    /**
     * 19) Toggle master mute state
     */
    toggleMasterMute() {
      this.setMasterMute(!this.isMasterMuted);
    },

    /**
     * 19a) Restore mute state from localStorage (called on initialization)
     */
    restoreMuteStateFromStorage() {
      // This is now mostly handled in the state initialization
      // But we still need to apply the mute state to any active players
      // that might have been created before this was called
      if (this.isMasterMuted) {
        // Apply volume changes to ensure any active players are muted
        this.applyVolumeChangesToActivePlayers();

        // Don't stop music, just keep it muted so it can resume when unmuted
      }
    },

    /**
     * 20) Apply volume changes to currently playing audio
     */
    applyVolumeChangesToActivePlayers() {
      try {
        // Update active music volume
        if (this.activeAudio.music) {
          // When muted, always use 0 volume regardless of stored volume
          // When unmuted, use stored custom volume if available, otherwise use global volume
          const targetVolume = this.isMasterMuted
            ? 0
            : (this.activeMusicVolume ?? this.linearMusicVolume);
          this.activeAudio.music.volume.value = Tone.gainToDb(targetVolume);
        }

        // Update active speech volume
        if (this.activeAudio.speech) {
          this.activeAudio.speech.volume.value = Tone.gainToDb(
            this.linearSpeechVolume
          );
        }
      } catch (error) {
        console.warn('Error updating volumes for active players:', error);
      }
    },

    /**
     * 21) Garbage collection for audio buffers and references.
     *     You might remove or dispose buffers that are no longer needed.
     */
    garbageCollect() {
      // Example approach: clear references if no longer needed
      // for (const key in this.audioBuffers) {
      //   // If your logic decides a buffer is no longer needed, remove it.
      //   delete this.audioBuffers[key]
      // }
    },

    /**
     * 22) Game transition management - prepare for game switch
     */
    async prepareGameTransition(newGameContext: string) {
      this.isTransitioning = true;

      // Stop all current audio
      this.stopAllAudio();

      // Clear any pending audio queue items from the previous game
      this.audioQueue = [];

      // Only clear audio buffers when switching to a different game.
      // When replaying the same game, preserve buffers to avoid
      // re-downloading and re-decoding audio files.
      if (this.currentGameContext === newGameContext) {
        console.log(
          `🎵 Same game context (${newGameContext}), preserving audio buffers`
        );
      } else {
        this.clearAllAudioBuffers();
      }

      // Update game context
      this.currentGameContext = newGameContext;

      // Reset speech state
      this.isSpeechPlaying = false;

      // Reset music volume to original, but only if not muted
      if (!this.isMasterMuted) {
        this.musicVolume = this.originalMusicVolume;
      }
    },

    /**
     * 23) Complete game transition - ready for new game audio
     */
    completeGameTransition() {
      this.isTransitioning = false;

      // Process any queued audio for the new game
      if (this.audioQueue.length > 0) {
        setTimeout(() => {
          if (this.isInitialized && !this.isInitializing) {
            this.processAudioQueue();
          }
        }, 100);
      }
    },

    /**
     * 24) Enhanced stop all audio with transition safety
     */
    stopAllAudioForTransition() {
      this.stopMusic();

      // Stop speech
      this.stopSpeech();

      // Cancel any scheduled Tone.js events
      Tone.Transport.cancel();
    },

    /**
     * 25) Enhanced play music with transition safety
     */
    async playMusicSafe(
      keyOrUrl: string,
      options: {
        loop?: boolean;
        volume?: number;
        loopStart?: number;
        loopEnd?: number;
        fadeInDuration?: number;
        crossFadeDuration?: number;
      } = {}
    ) {
      // Don't start new music if we're transitioning
      if (this.isTransitioning) {
        this.audioQueue.push({
          type: 'music',
          action: () => this.playMusic(keyOrUrl, options),
        });
        return;
      }

      return this.playMusic(keyOrUrl, options);
    },

    /**
     * 26) Game-specific buffer management
     */
    getGameAudioBuffer(
      gameContext: string,
      key: string
    ): Tone.ToneAudioBuffer | null {
      return (
        this.gameAudioBuffers[gameContext]?.[key] ||
        this.audioBuffers[key] ||
        null
      );
    },

    /**
     * 27) Store game-specific audio buffer
     */
    setGameAudioBuffer(
      gameContext: string,
      key: string,
      buffer: Tone.ToneAudioBuffer
    ) {
      if (!this.gameAudioBuffers[gameContext]) {
        this.gameAudioBuffers[gameContext] = {};
      }
      this.gameAudioBuffers[gameContext][key] = buffer;

      // Also store in general buffers for backward compatibility
      this.audioBuffers[key] = buffer;
    },

    /**
     * 28) Clear buffers for a specific game
     */
    clearGameAudioBuffers(gameContext: string) {
      if (this.gameAudioBuffers[gameContext]) {
        // Dispose of buffers to free memory
        Object.values(this.gameAudioBuffers[gameContext]).forEach((buffer) => {
          try {
            buffer.dispose();
          } catch (error) {
            console.warn('Error disposing buffer:', error);
          }
        });

        delete this.gameAudioBuffers[gameContext];
      }
    },

    /**
     * 29) Clear all cached audio buffers to force fresh loading
     */
    clearAllAudioBuffers() {
      // Dispose of all buffers to free memory
      Object.values(this.audioBuffers).forEach((buffer) => {
        try {
          buffer.dispose();
        } catch (error) {
          console.warn('Error disposing buffer:', error);
        }
      });

      // Clear all buffer caches
      this.audioBuffers = {};
      this.gameAudioBuffers = {};
    },

    /**
     * 30) Force reload audio for a specific URL (clears cache first)
     */
    async forceReloadAudio(keyOrUrl: string) {
      // Create the key that would be used
      let key = keyOrUrl;
      if (this.isUrl(keyOrUrl)) {
        const urlParts = keyOrUrl.split('/');
        const gameIndex = urlParts.findIndex((part) => part === 'games');
        const gameName =
          gameIndex >= 0 && gameIndex < urlParts.length - 1
            ? urlParts[gameIndex + 1]
            : 'unknown';
        const fileName = keyOrUrl.split('/').pop() || keyOrUrl;
        key = `${gameName}_${fileName}`;
      }

      // Remove existing buffer if it exists
      if (this.audioBuffers[key]) {
        try {
          this.audioBuffers[key].dispose();
        } catch (error) {
          console.warn('Error disposing existing buffer:', error);
        }
        delete this.audioBuffers[key];
      }

      // Now load it fresh
      return await this.ensureAudioBufferLoaded(keyOrUrl);
    },
  },
});
